import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { MoreVertical, Plus } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { notify } from "@/components/ui/dialogs";
import { get, put } from "@/utils/service";
import { apiMessage } from "@/utils/api";
import KpiCard, { Sparkline, change } from "@/components/platform/KpiCard";
import { countryName, flag } from "@/components/platform/countries";
import type { CountryStat } from "@/components/platform/WorldMap";
import AppSelect from "@/components/ui/app-select";

const WorldMap = lazy(() => import("@/components/platform/WorldMap"));

export interface Overview {
  period_days: number;
  kpis: {
    organizations: { total: number; active: number; suspended: number; new: number; new_previous: number; trend: number[] };
    users: { total: number; active: number; deactivated: number; signed_in: number; new: number; new_previous: number; trend: number[] };
    sign_ins: { total: number; previous: number; failed: number; trend: number[] };
    actions: { total: number; previous: number; trend: number[] };
  };
  usage: Array<{ date: string; sign_ins: number; actions: number; new_users: number }>;
  modules: Array<{ module: string; label: string; total: number; created_in_period: number }>;
  countries: CountryStat[];
  organizations: Array<{
    id: string; name: string; slug: string; status: string; country: string | null; city: string | null; created_at: string;
    members: number; active_users: number; actions: number; trend: number[]; last_activity_at: string | null;
  }>;
}

export const PERIODS = [
  { days: 7, label: "Last 7 days" }, { days: 30, label: "Last 30 days" }, { days: 90, label: "Last 3 months" }, { days: 180, label: "Last 6 months" },
];
const METRICS = [
  { key: "actions", label: "Actions" }, { key: "sign_ins", label: "Sign-ins" }, { key: "new_users", label: "New users" },
] as const;

const fmt = (n: number) => n.toLocaleString();
const ago = (iso: string | null) => {
  if (!iso) return "No activity yet";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? "Active today" : days === 1 ? "Active yesterday" : `Active ${days} days ago`;
};

/** Long periods are shown per week so the bars stay readable. */
function bucket(usage: Overview["usage"], key: (typeof METRICS)[number]["key"]) {
  const perWeek = usage.length > 45;
  if (!perWeek) {
    return usage.map((u) => ({ label: new Date(`${u.date}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" }), value: u[key] }));
  }
  const out: Array<{ label: string; value: number }> = [];
  for (let i = 0; i < usage.length; i += 7) {
    const slice = usage.slice(i, i + 7);
    out.push({ label: `W${out.length + 1}`, value: slice.reduce((n, u) => n + u[key], 0) });
  }
  return out;
}

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState<(typeof METRICS)[number]["key"]>("actions");
  const [region, setRegion] = useState("");
  const [data, setData] = useState<Overview | null>(null);
  const [menu, setMenu] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let live = true;
    get<{ data: Overview }>("/platform/analytics", { days })
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => notify(apiMessage(e, "Could not load platform analytics.")));
    return () => { live = false; };
  }, [days, refresh]);

  const bars = useMemo(() => (data ? bucket(data.usage, metric) : []), [data, metric]);
  const peak = bars.reduce((best, b, i) => (b.value > (bars[best]?.value ?? -1) ? i : best), 0);
  const orgs = (data?.organizations ?? []).filter((o) => !region || o.country === region);
  const totalRecords = (data?.modules ?? []).reduce((n, m) => n + m.total, 0);
  const createdRecords = (data?.modules ?? []).reduce((n, m) => n + m.created_in_period, 0);
  const period = PERIODS.find((p) => p.days === days)?.label.toLowerCase() ?? "";

  const toggleStatus = async (o: Overview["organizations"][number]) => {
    setMenu(null);
    const suspending = o.status === "active";
    const reason = suspending ? window.prompt(`Why is ${o.name} being suspended? Its members will be unable to sign in to it.`) : null;
    if (suspending && !reason) return;
    try {
      await put(`/platform/organizations/${o.id}`, { status: suspending ? "suspended" : "active", suspension_reason: reason });
      notify(`${o.name} ${suspending ? "suspended" : "reactivated"}`);
      setRefresh((k) => k + 1);
    } catch (e) { notify(apiMessage(e, "Could not change the status.")); }
  };

  if (!data) {
    return (
      <div className="space-y-5 p-4 sm:p-0">
        <PageHeader title="Dashboard" description="Usage across every organization" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-40 animate-pulse rounded-2xl bg-white" />)}</div>
        <div className="h-80 animate-pulse rounded-2xl bg-white" />
      </div>
    );
  }
  const k = data.kpis;

  return (
    <div className="space-y-5 p-4 sm:p-0">
      <PageHeader title="Dashboard" description="Usage across every organization: counts only, never their records">
        <AppSelect value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs">
          {PERIODS.map((p) => <option key={p.days} value={p.days}>{p.label}</option>)}
        </AppSelect>
        <button onClick={() => navigate("/platform/organizations?new=1")} className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-4 py-1.5 text-xs font-medium text-white hover:bg-black">
          <Plus className="h-3.5 w-3.5" />New Organization
        </button>
      </PageHeader>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard dark label="Organizations" value={fmt(k.organizations.total)} trend={k.organizations.trend}
          delta={change(k.organizations.new, k.organizations.new_previous)} deltaLabel={`${k.organizations.new} new, ${period}`}
          footer={`${k.organizations.active} active · ${k.organizations.suspended} suspended`} />
        <KpiCard label="Users" value={fmt(k.users.total)} trend={k.users.trend}
          delta={change(k.users.new, k.users.new_previous)} deltaLabel={`${k.users.new} new, ${period}`}
          footer={`${k.users.signed_in} signed in · ${k.users.deactivated} deactivated`} />
        <KpiCard label="Sign-ins" value={fmt(k.sign_ins.total)} trend={k.sign_ins.trend}
          delta={change(k.sign_ins.total, k.sign_ins.previous)} footer={`${k.sign_ins.failed} failed attempts`} />
        <KpiCard label="Actions" value={fmt(k.actions.total)} trend={k.actions.trend}
          delta={change(k.actions.total, k.actions.previous)} footer="Records created or changed in organizations" />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Platform usage</h2>
              <p className="text-xs text-gray-500">{METRICS.find((m) => m.key === metric)?.label} per {bars.length && bars[0].label.startsWith("W") ? "week" : "day"}, {period}</p>
            </div>
            <div className="inline-flex rounded-full bg-gray-100 p-1 text-xs">
              {METRICS.map((m) => (
                <button key={m.key} onClick={() => setMetric(m.key)} className={`rounded-full px-3 py-1 transition ${metric === m.key ? "bg-gray-900 text-white" : "text-gray-600"}`}>{m.label}</button>
              ))}
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bars} margin={{ top: 8, right: 4, bottom: 0, left: -24 }}>
                <CartesianGrid vertical={false} stroke="#f1f1f1" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={16} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} contentStyle={{ borderRadius: 12, border: "none", background: "#111827", color: "#fff", fontSize: 12 }} labelStyle={{ color: "#fff" }} itemStyle={{ color: "#fff" }} />
                <Bar dataKey="value" name={METRICS.find((m) => m.key === metric)?.label} radius={[4, 4, 0, 0]} maxBarSize={18}>
                  {bars.map((b, i) => <Cell key={b.label + i} fill={i === peak && b.value > 0 ? "#111827" : "#ff6d27"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="flex flex-col rounded-2xl bg-primary/10 p-5">
          <p className="text-4xl font-semibold tracking-tight text-gray-900">{fmt(totalRecords)} <span className="text-base font-normal text-gray-500">records</span></p>
          <p className="mt-1 text-xs text-gray-600"><span className="font-medium text-green-600">+{fmt(createdRecords)}</span> created {period}</p>
          <div className="mt-5 space-y-3">
            {data.modules.slice(0, 6).map((m) => (
              <div key={m.module}>
                <div className="flex items-center justify-between text-xs"><span className="font-medium text-gray-800">{m.label}</span><span className="text-gray-500">{fmt(m.total)}</span></div>
                <div className="mt-1 h-1.5 rounded-full bg-white"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${totalRecords ? Math.max(2, (m.total / totalRecords) * 100) : 0}%` }} /></div>
              </div>
            ))}
            {!totalRecords && <p className="text-xs text-gray-500">No records yet.</p>}
          </div>
        </section>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm xl:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-5">
            <div><h2 className="text-base font-semibold text-gray-900">Organizations</h2><p className="text-xs text-gray-500">Busiest first · activity {period}</p></div>
            <AppSelect value={region} onChange={(e) => setRegion(e.target.value)} className="rounded-full border border-gray-200 bg-gray-900 px-3 py-1.5 text-xs text-white">
              <option value="">All countries</option>
              {data.countries.map((c) => <option key={c.country} value={c.country}>{countryName(c.country)}</option>)}
            </AppSelect>
          </div>
          <div className="mt-3 overflow-x-auto grid-scroll">
            <table className="min-w-full text-xs">
              <thead><tr className="border-b border-gray-100 text-left text-gray-400">
                <th className="px-5 py-2 font-normal">Organization</th><th className="px-3 py-2 font-normal">Members</th><th className="px-3 py-2 font-normal">Active users</th>
                <th className="px-3 py-2 font-normal">Actions</th><th className="px-3 py-2 font-normal">Trend</th><th className="px-3 py-2 font-normal">Status</th><th className="px-3 py-2" />
              </tr></thead>
              <tbody>
                {orgs.slice(0, 8).map((o) => (
                  <tr key={o.id} className="border-b border-gray-50 hover:bg-gray-50/70">
                    <td className="px-5 py-3">
                      <button onClick={() => navigate(`/platform/analytics?org=${o.id}`)} className="flex items-center gap-3 text-left">
                        <span className="text-2xl leading-none">{flag(o.country)}</span>
                        <span><span className="block font-semibold text-gray-900">{o.name}</span><span className="text-gray-500">{[o.city, o.country ? countryName(o.country) : null].filter(Boolean).join(", ") || "Location not set"}</span></span>
                      </button>
                    </td>
                    <td className="px-3 py-3 text-gray-700">{fmt(o.members)}</td>
                    <td className="px-3 py-3 text-gray-700">{fmt(o.active_users)}</td>
                    <td className="px-3 py-3 font-semibold text-gray-900">{fmt(o.actions)}<span className="block font-normal text-gray-400">{ago(o.last_activity_at)}</span></td>
                    <td className="px-3 py-3"><Sparkline values={o.trend} /></td>
                    <td className="px-3 py-3"><span className={`rounded-md px-2 py-1 text-[11px] font-medium ${o.status === "active" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>{o.status === "active" ? "Active" : "Suspended"}</span></td>
                    <td className="relative px-3 py-3">
                      <button aria-label="Organization actions" onClick={() => setMenu(menu === o.id ? null : o.id)} className="rounded-md p-1 text-gray-400 hover:bg-gray-100"><MoreVertical className="h-4 w-4" /></button>
                      {menu === o.id && (
                        <div className="absolute right-3 top-10 z-20 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                          <button onClick={() => navigate(`/platform/analytics?org=${o.id}`)} className="block w-full px-3 py-2 text-left hover:bg-gray-50">View analytics</button>
                          <button onClick={() => navigate(`/platform/users?organization_id=${o.id}`)} className="block w-full px-3 py-2 text-left hover:bg-gray-50">View users</button>
                          <button onClick={() => toggleStatus(o)} className={`block w-full px-3 py-2 text-left hover:bg-gray-50 ${o.status === "active" ? "text-red-600" : "text-green-700"}`}>{o.status === "active" ? "Suspend" : "Reactivate"}</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!orgs.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-500">No organizations{region ? ` in ${countryName(region)}` : ""} yet.</td></tr>}
              </tbody>
            </table>
          </div>
          {orgs.length > 8 && <button onClick={() => navigate("/platform/organizations")} className="w-full border-t border-gray-100 py-3 text-xs font-medium text-primary hover:bg-gray-50">See all {orgs.length} organizations</button>}
        </section>

        <section className="flex flex-col rounded-2xl bg-gray-900 p-5 text-white">
          <div className="flex items-center justify-between"><h2 className="text-base font-semibold">Popularity by country</h2><span className="text-[11px] text-white/50">{data.countries.length} countr{data.countries.length === 1 ? "y" : "ies"}</span></div>
          <div className="mt-3 flex-1">
            <Suspense fallback={<div className="h-40 animate-pulse rounded-xl bg-white/10" />}><WorldMap data={data.countries} /></Suspense>
          </div>
          <div className="mt-3 space-y-2">
            {data.countries.slice(0, 4).map((c) => (
              <div key={c.country} className="flex items-center justify-between rounded-xl bg-white/5 px-3 py-2 text-xs">
                <span className="flex items-center gap-2"><span className="text-lg leading-none">{flag(c.country)}</span>{countryName(c.country)}</span>
                <span className="text-white/70">{c.organizations} org{c.organizations === 1 ? "" : "s"} · {fmt(c.members)} members</span>
              </div>
            ))}
            {!data.countries.length && <p className="text-xs text-white/60">Set each organization's country to see where the platform is used.</p>}
          </div>
        </section>
      </div>
    </div>
  );
}
